// Layer 3 (Transformation): cross-area correlation nudge for case cards.

import type { Case } from '@/versions/v1/schema/case';
import type { SignalRole } from '@/versions/v1/schema/tag-mapping';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { CASES, ISSUE_THREADS } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { CORRELATION_PAIRS } from '@/versions/v1/shared/bunker-hill-correlation-seed';
import { MONITORED_VARIABLES, TAG_MAPPING } from '@/versions/v1/shared/bunker-hill-spc';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { STREAMS } from '@/versions/v1/shared/bunker-hill-seed';
import { upstreamNodes, variableIdForCase } from '@/versions/v1/shared/threads';
import { upstreamChangeForCase, type UpstreamContext } from '@/versions/v1/shared/upstream.transform';

const MAPPING_BY_ROLE = new Map(TAG_MAPPING.map((row) => [row.role, row]));

function buildUpstreamAdj(): Map<string, Set<string>> {
  const adj = new Map<string, Set<string>>();
  for (const stream of STREAMS) {
    if (stream.from === 'external') continue;
    const set = adj.get(stream.to) ?? new Set();
    set.add(stream.from);
    adj.set(stream.to, set);
  }
  return adj;
}

const UPSTREAM = buildUpstreamAdj();

function subprocessForRole(role: string): string | undefined {
  return MAPPING_BY_ROLE.get(role as (typeof TAG_MAPPING)[number]['role'])?.subprocessId;
}

function bfsDepthUpstream(fromSub: string, toSub: string): number {
  if (fromSub === toSub) return 0;
  const queue: { node: string; depth: number }[] = [{ node: fromSub, depth: 0 }];
  const visited = new Set<string>([fromSub]);
  while (queue.length > 0) {
    const { node, depth } = queue.shift()!;
    for (const up of UPSTREAM.get(node) ?? []) {
      if (up === toSub) return depth + 1;
      if (!visited.has(up)) {
        visited.add(up);
        queue.push({ node: up, depth: depth + 1 });
      }
    }
  }
  return 0;
}

function stagePhrase(count: number): string {
  if (count <= 1) return 'one stage upstream';
  if (count === 2) return 'two stages upstream';
  return `${count} stages upstream`;
}

function signalLabel(role: string): string {
  const mapping = MAPPING_BY_ROLE.get(role as (typeof TAG_MAPPING)[number]['role']);
  if (!mapping) return role;
  const reading = readingByIdLookup(mapping.readingId ?? '');
  return reading?.label ?? mapping.instrumentTag ?? role;
}

function caseSubprocess(caseItem: Case): string | undefined {
  const variableId = variableIdForCase(caseItem);
  if (!variableId) return undefined;
  const monitored = MONITORED_VARIABLES.find((row) => row.id === variableId);
  if (!monitored) return undefined;
  const mapping = TAG_MAPPING.find((row) => row.seriesId === monitored.seriesId);
  return mapping?.subprocessId;
}

function signalRolesInThread(caseItem: Case, context: UpstreamContext): Set<SignalRole> {
  const roles = new Set<SignalRole>();
  const thread = ISSUE_THREADS.find((row) => row.caseIds.includes(caseItem.id));
  if (!thread) return roles;

  for (const caseId of thread.caseIds) {
    const peer = context.cases.find((row) => row.id === caseId) ?? CASES.find((row) => row.id === caseId);
    if (!peer) continue;
    const variableId = variableIdForCase(peer);
    if (variableId) {
      const monitored = MONITORED_VARIABLES.find((row) => row.id === variableId);
      const role = TAG_MAPPING.find((row) => row.seriesId === monitored?.seriesId)?.role;
      if (role) roles.add(role);
    }
    if (peer.id === 'case-smbs-fmea') roles.add('reagent-smbs-rate');
  }
  return roles;
}

function pairResolves(
  caseItem: Case,
  pair: (typeof CORRELATION_PAIRS)[number],
  context: UpstreamContext,
): boolean {
  const caseSub = caseSubprocess(caseItem);
  const primarySub = subprocessForRole(pair.primaryRole);
  const correlatedSub = subprocessForRole(pair.correlatedRole);
  if (!caseSub || !primarySub || !correlatedSub) return false;
  if (primarySub === correlatedSub) return false;

  const threadRoles = signalRolesInThread(caseItem, context);
  if (threadRoles.has(pair.correlatedRole as SignalRole)) return false;

  const caseUpstream = upstreamNodes(caseSub);
  return caseUpstream.has(correlatedSub) || caseUpstream.has(primarySub);
}

export type CorrelationNudge = {
  text: string;
};

export function correlationNudgeForCase(
  caseItem: Case,
  _state: SessionState,
  context: UpstreamContext,
): CorrelationNudge | null {
  const pair = CORRELATION_PAIRS.find((row) => pairResolves(caseItem, row, context));
  if (!pair) return null;
  const caseSub = caseSubprocess(caseItem);
  const correlatedSub = subprocessForRole(pair.correlatedRole);
  if (!caseSub || !correlatedSub) return null;
  const stages = bfsDepthUpstream(caseSub, correlatedSub);
  if (stages <= 0) return null;
  return {
    text: `also moving: ${signalLabel(pair.correlatedRole)}, ${stagePhrase(stages)}`,
  };
}

export function correlationLineForCase(
  caseItem: Case,
  state: SessionState,
  context?: UpstreamContext,
): string | null {
  const upstream = upstreamChangeForCase(caseItem, state, context);
  if (!upstream) return null;
  const nudge = context ? correlationNudgeForCase(caseItem, state, context) : null;
  const offset =
    upstream.offsetMinutes > 0 ? `, ${upstream.offsetMinutes} min before` : '';
  const base = `${upstream.label}${offset}`;
  if (!nudge) return base;
  return `${base} · ${nudge.text}`;
}

export function drawerCorrelationLine(
  caseItem: Case,
  state: SessionState,
  context?: UpstreamContext,
): string | null {
  return correlationLineForCase(caseItem, state, context);
}
